import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { X, Check, ZoomIn } from "lucide-react";

// Recadrage circulaire d'une photo : glisser pour positionner, curseur pour zoomer.
// Le rendu exporté est un carré 256x256 (affiché en rond partout sur le site).
const VIEW = 240; // diamètre du cercle de prévisualisation (px)

export default function AvatarCropper({ src, onCancel, onValidate, testid = "avatar-cropper" }) {
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1); // multiplicateur du cadrage "couvrir"
  const [off, setOff] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const im = new Image();
    im.onload = () => { setImg(im); setZoom(1); setOff({ x: 0, y: 0 }); };
    im.src = src;
  }, [src]);

  // Échelle d'affichage : l'image couvre toujours tout le cercle.
  const base = img ? Math.max(VIEW / img.naturalWidth, VIEW / img.naturalHeight) : 1;
  const scale = base * zoom;
  const dispW = img ? img.naturalWidth * scale : 0;
  const dispH = img ? img.naturalHeight * scale : 0;

  const clamp = useCallback((o, z = zoom) => {
    if (!img) return o;
    const s = base * z;
    const w = img.naturalWidth * s, h = img.naturalHeight * s;
    return {
      x: Math.min(0, Math.max(VIEW - w, o.x)),
      y: Math.min(0, Math.max(VIEW - h, o.y)),
    };
  }, [img, base, zoom]);

  useEffect(() => { setOff((o) => clamp(o)); }, [zoom, clamp]);

  const onPointerDown = (e) => {
    e.preventDefault();
    drag.current = { sx: e.clientX, sy: e.clientY, ox: off.x, oy: off.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const d = drag.current;
    setOff(clamp({ x: d.ox + (e.clientX - d.sx), y: d.oy + (e.clientY - d.sy) }));
  };
  const onPointerUp = () => { drag.current = null; };

  const validate = () => {
    if (!img) return;
    const size = 256;
    const canvas = document.createElement("canvas");
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext("2d");
    const sx = -off.x / scale, sy = -off.y / scale, sSize = VIEW / scale;
    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, size, size);
    onValidate(canvas.toDataURL("image/jpeg", 0.88));
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto overscroll-contain bg-black/75 p-4 aca-modal" data-testid={testid}>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="mt-10 w-full max-w-sm rounded-2xl border border-white/10 bg-elevated p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-700 text-white">Ajuster la photo</h3>
          <button onClick={onCancel} data-testid={`${testid}-close`} className="text-ink-secondary hover:text-white"><X size={20} /></button>
        </div>
        <p className="mb-4 text-xs text-ink-secondary">Faites glisser la photo pour la positionner dans le rond, zoomez avec le curseur.</p>

        <div className="flex justify-center">
          <div
            ref={boxRef}
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
            className="relative touch-none select-none overflow-hidden rounded-full border-2 border-cyan/60 bg-void shadow-[0_0_20px_rgba(0,240,255,0.2)]"
            style={{ width: VIEW, height: VIEW, cursor: "grab" }}
            data-testid={`${testid}-viewport`}
          >
            {img && (
              <img src={src} alt="" draggable={false}
                className="pointer-events-none absolute max-w-none"
                style={{ width: dispW, height: dispH, left: off.x, top: off.y }} />
            )}
          </div>
        </div>

        <label className="mt-5 flex items-center gap-3">
          <ZoomIn size={16} className="shrink-0 text-cyan" />
          <input type="range" min="1" max="3" step="0.01" value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            data-testid={`${testid}-zoom`}
            className="w-full accent-cyan" />
        </label>

        <div className="mt-5 flex gap-3">
          <button onClick={validate} data-testid={`${testid}-validate`}
            className="aca-btn-primary flex-1">
            <Check size={14} /> Valider
          </button>
          <button onClick={onCancel} data-testid={`${testid}-cancel`}
            className="aca-btn-secondary flex-1">
            Annuler
          </button>
        </div>
      </motion.div>
    </div>
  );
}
